import { type ReactNode } from 'react';

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => ReactNode;
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  empty?: string;
  rowAction?: (row: T) => ReactNode;
  // How the scroll box is sized. Default 'h-full' fills a height-bounded parent
  // (GroupedGrid). Pages that stack several grids pass a 'max-h-[…]' cap instead so
  // each one scrolls internally with a reachable horizontal scrollbar.
  heightClass?: string;
}

export function DataGrid<T extends Record<string, unknown>>({
  columns,
  rows,
  loading,
  empty = 'No records.',
  rowAction,
  heightClass = 'h-full',
}: Props<T>) {
  return (
    // Bounded height + overflow-auto keeps BOTH scrollbars inside this box and on
    // screen. Header sticks while the body scrolls vertically.
    <div className={`${heightClass} overflow-auto rounded-lg border border-border bg-card shadow-sm`}>
      <table className="min-w-full divide-y divide-border">
        <thead className="sticky top-0 z-10 bg-secondary/60">
          <tr>
            {rowAction ? <th className="table-cell w-10" /> : null}
            {columns.map((c) => (
              <th
                key={c.key}
                className="table-cell text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {loading ? (
            <tr>
              <td className="table-cell" colSpan={columns.length + 1}>
                Loading…
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td className="table-cell text-muted-foreground" colSpan={columns.length + 1}>
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr key={i} className="hover:bg-secondary/30">
                {rowAction ? <td className="table-cell">{rowAction(row)}</td> : null}
                {columns.map((c) => (
                  <td key={c.key} className="table-cell">
                    {c.render ? c.render(row) : String(row[c.key] ?? '')}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
